// ============================================================
// MaintenancePlans.jsx — TAM DOSYA
// Değişiklikler:
// 1. Varsayılan tab: "Bu Hafta" (soon)
// 2. Sıralama: en yakın vade önce (overdue en önce, sonra yaklaşan)
// 3. "Her 1 günde bir" — frequency_days=1 olanlar için "Tek seferlik" göster
// 4. Atanmadı yerine boş göster
// ============================================================

import { useState, useEffect } from "react";
import {
  getMaintenancePlans,
  getOverduePlans,
  completeMaintenancePlan,
} from "../api/client";
import {
  ArrowLeft,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clock,
  User,
} from "lucide-react";

function daysDiff(dateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dateStr);
  due.setHours(0, 0, 0, 0);
  return Math.ceil((due - today) / (1000 * 60 * 60 * 24));
}

function PlanCard({ plan, onComplete }) {
  const [expanded, setExpanded] = useState(false);
  const [doneBy, setDoneBy] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const days = daysDiff(plan.next_due);
  const isOverdue = days < 0;
  const isDueSoon = days >= 0 && days <= 7;
  const isSingleUse = plan.frequency_days <= 1;

  const handleComplete = async () => {
    setLoading(true);
    await completeMaintenancePlan({ plan_id: plan.id, done_by: doneBy, note });
    setLoading(false);
    setExpanded(false);
    onComplete();
  };

  return (
    <div
      className={`rounded-xl border p-5 mb-3 transition-all ${
        isOverdue
          ? "bg-red-900/20 border-red-500/50"
          : isDueSoon
            ? "bg-yellow-900/20 border-yellow-500/50"
            : "bg-slate-900 border-slate-700"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {isOverdue && (
              <span className="flex items-center gap-1 text-xs text-red-400 bg-red-500/20 px-2 py-0.5 rounded-full border border-red-500/30">
                <AlertTriangle className="w-3 h-3" />
                {Math.abs(days)} gün gecikmiş
              </span>
            )}
            {isDueSoon && !isOverdue && (
              <span className="flex items-center gap-1 text-xs text-yellow-400 bg-yellow-500/20 px-2 py-0.5 rounded-full border border-yellow-500/30">
                <Clock className="w-3 h-3" />
                {days === 0 ? "Bugün" : `${days} gün kaldı`}
              </span>
            )}
            {!isOverdue && !isDueSoon && (
              <span className="text-xs text-green-400 bg-green-500/20 px-2 py-0.5 rounded-full border border-green-500/30">
                {days} gün kaldı
              </span>
            )}
            <span className="text-xs text-slate-400 bg-slate-800 px-2 py-0.5 rounded-full">
              {isSingleUse
                ? "Tek seferlik"
                : `Her ${plan.frequency_days} günde bir`}
            </span>
          </div>
          <h3 className="font-semibold text-white text-sm mb-1">
            {plan.title}
          </h3>
          <p className="text-xs text-slate-400 mb-1">{plan.machine_name}</p>
          {plan.description && (
            <p className="text-xs text-slate-500">{plan.description}</p>
          )}
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs px-3 py-1.5 rounded-lg bg-blue-600/20 border border-blue-500/30 text-blue-400 hover:bg-blue-600/30 shrink-0"
        >
          Tamamla
        </button>
      </div>

      <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
        <span className="flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          Vade: {new Date(plan.next_due).toLocaleDateString("tr-TR")}
        </span>
        {plan.assigned_to && plan.assigned_to !== "Atanmadi" && (
          <span className="flex items-center gap-1">
            <User className="w-3 h-3" />
            {plan.assigned_to}
          </span>
        )}
      </div>

      {plan.last_done && (
        <p className="text-xs text-slate-600 mt-1">
          Son bakım: {new Date(plan.last_done).toLocaleDateString("tr-TR")}
        </p>
      )}

      {expanded && (
        <div className="mt-4 border-t border-slate-700 pt-4 space-y-3">
          <div>
            <label className="text-xs text-slate-400 block mb-1">
              Yapan Kişi
            </label>
            <input
              type="text"
              value={doneBy}
              onChange={(e) => setDoneBy(e.target.value)}
              placeholder="Ad Soyad"
              className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">
              Bakım Notu
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Yapılan işlemler, gözlemler..."
              rows={3}
              className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 resize-none"
            />
          </div>
          <button
            onClick={handleComplete}
            disabled={loading || !doneBy}
            className="w-full py-2 rounded-lg bg-green-500/20 border border-green-500/50 text-green-400 text-sm font-medium hover:bg-green-500/30 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {loading ? "Kaydediliyor..." : "Bakımı Tamamlandı Olarak İşaretle"}
          </button>
        </div>
      )}
    </div>
  );
}

export default function MaintenancePlans({ onBack }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("soon"); // Varsayılan: Bu Hafta
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchPlans = async () => {
    try {
      const res = await getMaintenancePlans();
      // Sıralama: önce gecikmişler (en eski önce), sonra yaklaşanlar, sonra diğerleri
      const sorted = [...res.data].sort((a, b) => {
        const da = daysDiff(a.next_due);
        const db = daysDiff(b.next_due);
        return da - db; // küçük (negatif = geçmiş) önce
      });
      setPlans(sorted);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await fetch("/api.php?action=maintenance_history");
      const data = await res.json();
      if (data.success) setHistory(data.data);
    } catch (e) {
      console.error("Geçmiş yüklenemedi", e);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
    fetchHistory();
  }, []);

  const filtered = plans.filter((p) => {
    const days = daysDiff(p.next_due);
    if (filter === "overdue") return days < 0;
    if (filter === "soon") return days >= 0 && days <= 7;
    return p.status === "active";
  });

  const overdueCount = plans.filter((p) => daysDiff(p.next_due) < 0).length;
  const soonCount = plans.filter((p) => {
    const d = daysDiff(p.next_due);
    return d >= 0 && d <= 7;
  }).length;

  return (
    <div className="min-h-screen bg-slate-950">
      <div className="border-b border-slate-800 bg-slate-900/80 backdrop-blur sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-base font-bold text-white flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-400" />
              Planlı Bakım Takvimi
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {overdueCount > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                {overdueCount} gecikmiş
              </span>
            )}
            {soonCount > 0 && (
              <span className="bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                {soonCount} yaklaşan
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex gap-2 mb-6">
          {[
            { key: "soon", label: `Bu Hafta (${soonCount})` },
            { key: "overdue", label: `Gecikmiş (${overdueCount})` },
            { key: "all", label: "Tümü" },
            { key: "history", label: `Geçmiş (${history.length})` },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${
                filter === f.key
                  ? f.key === "overdue"
                    ? "bg-red-600 text-white"
                    : "bg-blue-600 text-white"
                  : "bg-slate-800 text-slate-400 hover:bg-slate-700"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {filter === "history" ? (
          historyLoading ? (
            <div className="flex justify-center py-20">
              <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-20">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <p className="text-slate-400">Henüz tamamlanan bakım kaydı yok</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((log) => (
                <div
                  key={log.id}
                  className="bg-slate-900 border border-slate-700 rounded-xl p-4 flex items-start justify-between gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
                      <span className="text-white font-medium text-sm">
                        {log.title}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 mb-1">
                      {log.machine_name} · {log.line_type}
                    </p>
                    {log.done_by && (
                      <p className="text-xs text-slate-500 flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {log.done_by}
                      </p>
                    )}
                    {log.note && (
                      <p className="text-xs text-slate-500 italic mt-1">
                        "{log.note}"
                      </p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-green-400 font-medium">
                      {new Date(log.done_at).toLocaleDateString("tr-TR")}
                    </p>
                    <p className="text-xs text-slate-500">
                      {new Date(log.done_at).toLocaleTimeString("tr-TR", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <p className="text-slate-400">Bu kategoride bakım planı yok</p>
          </div>
        ) : (
          filtered.map((plan) => (
            <PlanCard key={plan.id} plan={plan} onComplete={fetchPlans} />
          ))
        )}
      </div>
    </div>
  );
}
